import { useEffect, useRef, useState } from "react";
import { Button, Drawer, Input } from "antd";
import { PictureOutlined } from "@ant-design/icons";

import { askOperatorHelp, OperatorHelpTurn } from "../axios/routes/operatorHelp";
import { HelpLocation, HelpLocationCard } from "./HelpLocationCard";
import { COLORS } from "../styled/basic";

const EXAMPLES = [
  "Where can I change the sponsor logo for a team?",
  "How do I schedule new games?",
  "Why is my image I am uploading to a sponsor frame not saving?",
  "How do I delete an operator or change access?",
  "Where do I turn send-to-print on for a game?",
  "Where are data moment captions edited?",
  "How do I finish a game?",
];

type ChatLine = OperatorHelpTurn & {
  id: string;
  imageUrl?: string;
  location?: HelpLocation | null;
};

type PendingImage = { previewUrl: string; data: string };

function lineId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function shrinkScreenshot(file: File): Promise<PendingImage> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const maxEdge = 1280;
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error("Could not read that image"));
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.72);
      URL.revokeObjectURL(url);
      const data = dataUrl.split(",")[1] || "";
      if (!data || data.length > 900000) {
        reject(new Error("Image is too large. Use a smaller screenshot."));
        return;
      }
      resolve({ previewUrl: dataUrl, data });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Use a PNG or JPG screenshot"));
    };
    img.src = url;
  });
}

export function OperatorHelpDrawer({
  open,
  onClose,
  viewerEmail,
}: {
  open: boolean;
  onClose: () => void;
  viewerEmail: string;
}): JSX.Element {
  const [lines, setLines] = useState<ChatLine[]>([]);
  const [draft, setDraft] = useState("");
  const [pendingImage, setPendingImage] = useState<PendingImage | null>(null);
  const [imageError, setImageError] = useState("");
  const [busy, setBusy] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const node = scroller.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [lines, busy, open]);

  const ask = async (preset?: string) => {
    const question = (preset ?? draft).trim();
    if (busy || !viewerEmail) return;
    if (!question && !pendingImage) return;
    const history = lines.map(({ role, content }) => ({ role, content }));
    const image = pendingImage;
    const shown = question || "What is happening in this screenshot?";
    setDraft("");
    setPendingImage(null);
    setImageError("");
    setLines((prev) => [
      ...prev,
      {
        id: lineId(),
        role: "user",
        content: image ? `${shown} (screenshot attached)` : shown,
        imageUrl: image?.previewUrl,
      },
    ]);
    setBusy(true);
    try {
      const result = await askOperatorHelp(
        viewerEmail,
        shown,
        history,
        image ? { mediaType: "image/jpeg", data: image.data } : undefined,
      );
      setLines((prev) => [...prev, {
        id: lineId(),
        role: "assistant",
        content: result.answer,
        location: result.location,
      }]);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Help could not answer right now";
      setLines((prev) => [...prev, { id: lineId(), role: "assistant", content: message }]);
    } finally {
      setBusy(false);
    }
  };

  const onPickImage = async (file: File | undefined) => {
    if (!file) return;
    setImageError("");
    try {
      setPendingImage(await shrinkScreenshot(file));
    } catch (err) {
      setPendingImage(null);
      setImageError(err instanceof Error ? err.message : "Could not read that image");
    }
  };

  return (
    <Drawer
      title={(
        <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>
          Help
        </span>
      )}
      placement="right"
      width={440}
      onClose={onClose}
      visible={open}
      bodyStyle={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <div
        ref={scroller}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 16,
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        {lines.length === 0 ? (
          <div>
            <div style={{ color: "#111827", fontSize: 14, lineHeight: 1.5, marginBottom: 8 }}>
              Ask where a screen is or how a feature works. When the answer is a
              place in Operator, Help shows that screen and a link that opens it.
              Help does not change settings or look up a live game.
            </div>
            <div style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.45, marginBottom: 12 }}>
              Add a screenshot when something will not save, looks wrong, or you
              are not sure which page you are on.
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
              Try a question
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {EXAMPLES.map((example) => (
                <button
                  key={example}
                  type="button"
                  disabled={busy}
                  onClick={() => setDraft(example)}
                  style={{
                    textAlign: "left",
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: "8px 10px",
                    color: "#1f2937",
                    fontSize: 13,
                    lineHeight: 1.4,
                    cursor: "pointer",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {lines.map((line) => (
          <div
            key={line.id}
            style={{
              marginBottom: 12,
              display: "flex",
              justifyContent: line.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            <div
              style={{
                maxWidth: "88%",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 14,
                lineHeight: 1.45,
                padding: "10px 12px",
                borderRadius: 10,
                background: line.role === "user" ? COLORS.BLUE_MAIN : "#f3f4f6",
                color: line.role === "user" ? "#fff" : "#111827",
              }}
            >
              {line.imageUrl ? (
                <img
                  src={line.imageUrl}
                  alt="Screenshot attached to this question"
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    borderRadius: 6,
                    marginBottom: 8,
                  }}
                />
              ) : null}
              {line.content.replace(" (screenshot attached)", "")}
              {line.role === "assistant" && line.location ? (
                <HelpLocationCard location={line.location} onOpen={onClose} />
              ) : null}
            </div>
          </div>
        ))}
        {busy ? (
          <div style={{ color: "#6b7280", fontSize: 13 }}>Looking that up…</div>
        ) : null}
      </div>
      <div style={{ borderTop: "1px solid #e5e7eb", padding: 12 }}>
        {pendingImage ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <img
              src={pendingImage.previewUrl}
              alt="Screenshot to send"
              style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6 }}
            />
            <Button type="link" onClick={() => setPendingImage(null)} disabled={busy}>
              Remove image
            </Button>
          </div>
        ) : null}
        {imageError ? (
          <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: 8 }}>{imageError}</div>
        ) : null}
        <Input.TextArea
          value={draft}
          rows={3}
          maxLength={1500}
          placeholder="Where is something, or how does a feature work?"
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={(e) => {
            if (e.shiftKey) return;
            e.preventDefault();
            ask().catch(() => undefined);
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                onPickImage(file).catch(() => undefined);
              }}
            />
            <Button
              icon={<PictureOutlined />}
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              Add image
            </Button>
          </div>
          <Button
            type="primary"
            loading={busy}
            disabled={!draft.trim() && !pendingImage}
            onClick={() => { ask().catch(() => undefined); }}
          >
            Ask
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
